import { Action, Items, ParagraphItem } from '../Card';
import { Link } from '@xo-union/tk-component-buttons';
import React, { useContext } from 'react';
import { DisplayLink } from '@xo-union/tk-ui-links';
import { Body1, Body2 } from '@xo-union/tk-ui-typography';
import { useHistory } from 'react-router-dom';
import striptags from 'striptags';

import { Maybe, PagesFragment, RegistrySummary } from '../../generated/graphql';
import { pageSettingsRoute } from '../../helpers/routes';
import { TrackingContext } from '../../TrackingContext';
import { ShopByPriceModule } from '../PreviewPage/ShopByPriceModule';
import styles from './registryPageCard.scss';

interface Props {
  page: PagesFragment;
  preview: boolean;
  onRegistryItemAdded: () => void;
  registrySummary?: RegistrySummary | null;
  registryNote?: Maybe<string>;
}

export const RegistryCardContent: React.FC<Props> = ({
  registrySummary,
  registryNote,
  onRegistryItemAdded,
  page,
  preview = false,
}) => {
  const { track } = useContext(TrackingContext);
  const history = useHistory();

  if (!registrySummary) {
    return null;
  }

  const centsToDollarString = (cents: number | null | undefined) => {
    if (!cents) {
      // true for 0, null, and undefined
      cents = 0;
    }

    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const clickManageRegistryEvent = () => {
    track('WWS Dashboard Interaction', {
      action: 'update',
      actionName: 'registry',
    });
  };

  const onEditNote = () => {
    history.push(pageSettingsRoute(page.id, preview));
    track('Item Action Initiated', {
      action: 'edit',
      actionName: 'Edit Page',
      page: page.title,
      item: null,
    });
  };

  const hasRegistrySummary = registrySummary && Object.keys(registrySummary).length;
  const totalGiftsRequested =
    typeof registrySummary?.totalActiveGiftsRequested === 'string'
      ? parseInt(registrySummary.totalActiveGiftsRequested, 10)
      : 0;
  const totalGiftsRequestedLabel = totalGiftsRequested === 1 ? 'gift' : 'gifts';

  const cashFundsRequestedLabel = registrySummary?.totalCashFunds === 1 ? 'cash fund' : 'cash funds';

  return (
    <>
      <Items>
        {registryNote && (
          <ParagraphItem
            className={styles.registryNote}
            text={striptags(registryNote).replace(/&nbsp;/gi, ' ')}
            header="Registry Note"
            onClick={onEditNote}
            showGrabber={false}
          />
        )}
        <div className={styles.registryCardContent}>
          <div className={styles.registrySummaryWrapper}>
            {hasRegistrySummary && (
              <div className={styles.info}>
                <div className={styles.addedGiftsInfo}>
                  <Body1 bold className={styles.giftsAddedValue}>
                    {totalGiftsRequested}
                  </Body1>
                  <Body1 className={styles.infoLabel}>{totalGiftsRequestedLabel} added</Body1>
                </div>
                <Body2 className={styles.infoSub}>
                  {typeof registrySummary?.totalGiftsReceived === 'number' ? registrySummary.totalGiftsReceived : 0}{' '}
                  purchased
                </Body2>
              </div>
            )}
            {hasRegistrySummary && (
              <div className={styles.info}>
                <div className={styles.addedGiftsInfo}>
                  <Body1 bold className={styles.giftsAddedValue}>
                    {registrySummary?.totalCashFunds}
                  </Body1>
                  <Body1 className={styles.infoLabel}>{cashFundsRequestedLabel} added</Body1>
                </div>
                <Body2 className={styles.infoSub}>
                  {centsToDollarString(registrySummary?.totalCashCentsReceived)} contributed
                </Body2>
              </div>
            )}
          </div>
          <DisplayLink
            className={styles.manageRegistryLink}
            data-testid="manage-registry-link"
            href="/registry"
            size="md"
            target={'_blank'}
            color="secondary-alternative"
            onClick={clickManageRegistryEvent}
          >
            Manage registry
          </DisplayLink>
          <Link
            className={styles.manageRegistryButton}
            data-testid="manage-registry-button"
            href="/registry"
            size="sm"
            target={'_blank'}
            color="secondary-alternative"
            onClick={clickManageRegistryEvent}
          >
            Manage registry
          </Link>
        </div>
        <div className={styles.shopByPrice}>
          <ShopByPriceModule isPreview={false} onItemAdded={onRegistryItemAdded} />
        </div>
      </Items>
      {!registryNote && (
        <>
          <div className={styles.dividerLine} />
          <div className={styles.registryNoteCTA}>
            <Action iconName="add_circle" onClick={onEditNote}>
              Add a registry note
            </Action>
          </div>
        </>
      )}
    </>
  );
};
